import type { EntitySummary, EventSummary, NarrativeStatus, NarrativeSummary } from './types';

export interface TimelineFilters {
  locationIds: string[];
  includeSublocations: boolean;
  characterIds: string[];
  organizationIds: string[];
  dateRange?: { start: string; end: string };
  narrativeStatus: NarrativeStatus[];
}

/**
 * Apply all active filters to event list
 */
export function filterEventsByFilters(
  events: EventSummary[],
  filters: TimelineFilters,
  locations: EntitySummary[]
): EventSummary[] {
  return events.filter((event) => {
    // Location filter (primary OR secondary, with optional subtree matching)
    if (filters.locationIds.length > 0) {
      const matchedLocationIds = filters.includeSublocations
        ? getLocationIdsWithDescendants(filters.locationIds, locations)
        : filters.locationIds;

      const eventLocationIds = [
        event.locationId,
        ...(event.locationIds || []),
      ].filter((id): id is string => !!id);

      const hasLocationMatch = eventLocationIds.some((locId) =>
        matchedLocationIds.includes(locId)
      );

      if (!hasLocationMatch) return false;
    }

    // Character filter
    if (filters.characterIds.length > 0) {
      const hasCharacterMatch = filters.characterIds.some((charId) =>
        event.characterIds?.includes(charId)
      );
      if (!hasCharacterMatch) return false;
    }

    // Organization filter
    if (filters.organizationIds.length > 0) {
      const hasOrgMatch = filters.organizationIds.some((orgId) =>
        event.organizationIds?.includes(orgId)
      );
      if (!hasOrgMatch) return false;
    }

    // Date range filter
    if (filters.dateRange && event.date) {
      const eventDate = event.date;
      if (
        eventDate < filters.dateRange.start ||
        eventDate > filters.dateRange.end
      ) {
        return false;
      }
    }

    // Narrative status filter (computed)
    if (filters.narrativeStatus.length > 0) {
      // Note: This would require scenes data; for now we'll skip this filter
      // and implement it at the component level
    }

    return true;
  });
}

/**
 * Get all descendant location IDs for subtree matching
 */
export function getLocationDescendants(
  locationId: string,
  locations: EntitySummary[]
): string[] {
  const descendants: string[] = [];

  const findChildren = (parentId: string) => {
    const children = locations.filter((loc) => loc.parentId === parentId);
    children.forEach((child) => {
      descendants.push(child.id);
      findChildren(child.id);
    });
  };

  findChildren(locationId);
  return descendants;
}

/**
 * Get location IDs including all descendants for subtree matching
 */
function getLocationIdsWithDescendants(
  locationIds: string[],
  locations: EntitySummary[]
): string[] {
  const result = new Set(locationIds);

  locationIds.forEach((locId) => {
    const descendants = getLocationDescendants(locId, locations);
    descendants.forEach((desc) => result.add(desc));
  });

  return Array.from(result);
}

/**
 * Build location hierarchy tree for filtering
 */
export function getLocationHierarchy(locations: EntitySummary[]): EntitySummary[] {
  // Return root locations (those without parentId or where parentId is empty)
  return locations.filter((loc) => !loc.parentId);
}

/**
 * Count events per location for map view (uses primaryLocationId only)
 */
export function aggregateEventsByLocation(
  events: EventSummary[]
): Record<string, number> {
  const aggregation: Record<string, number> = {};

  events.forEach((event) => {
    const locId = event.locationId;
    if (locId) {
      aggregation[locId] = (aggregation[locId] || 0) + 1;
    }
  });

  return aggregation;
}

/**
 * Calculate narrative status based on scenes and completion flag
 * Rules:
 * - sceneCount === 0 → "notWritten"
 * - sceneCount > 0 && event.isNarrativelyComplete → "complete"
 * - sceneCount > 0 && !isNarrativelyComplete → "partial"
 */
export function calculateNarrativeStatus(
  event: EventSummary,
  scenes: NarrativeSummary[]
): NarrativeStatus {
  const relatedScenes = scenes.filter((scene) =>
    scene.eventIds?.includes(event.id)
  );
  const sceneCount = relatedScenes.length;

  if (sceneCount === 0) {
    return 'notWritten';
  }

  if (event.isNarrativelyComplete) {
    return 'complete';
  }

  return 'partial';
}

/**
 * Extract lat/lng coordinates from location
 * Returns null if location has no coordinates
 */
export function getLocationCoordinates(location: EntitySummary): { lat: number; lng: number } | null {

  // For now, locations don't have lat/lng fields yet
  // This will be implemented when location coordinates are added
  return null;
}

/**
 * Get polygon geometry for a location (for hierarchical regions/kingdoms)
 * Returns null if location has no polygon data
 */
export function getLocationPolygon(location: EntitySummary): unknown | null {

  // For now, locations don't have polygon geometry
  // This will be implemented when polygon support is added
  return null;
}
